import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/shared/ui/atoms/Button"
import { Badge } from "@/shared/ui/atoms/Badge"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/ui/atoms/Command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/atoms/Popover"

export interface ComboboxOption {
  value: string
  label: string
  icon?: React.ComponentType<{ className?: string }>
}

export interface ComboboxProps {
  multiple?: boolean
  value?: string
  values?: string[]
  onValueChange?: (value: string) => void
  onValuesChange?: (values: string[]) => void
  options: ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  allValue?: string
  allLabel?: string
  itemNounSingular?: string
  itemNounPlural?: string
  icon?: React.ComponentType<{ className?: string }>
  className?: string
  triggerClassName?: string
  disabled?: boolean
  align?: "start" | "center" | "end"
}

export const Combobox = React.memo(function Combobox({
  multiple = false,
  value,
  values = [],
  onValueChange,
  onValuesChange,
  options,
  placeholder = "Seleccionar...",
  searchPlaceholder = "Buscar...",
  emptyText = "No se encontraron resultados.",
  allValue = "ALL",
  allLabel,
  itemNounSingular,
  itemNounPlural,
  icon: TriggerIcon,
  className,
  triggerClassName,
  disabled = false,
  align = "start",
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)

  // Multi-select helpers
  const isAllSelected = React.useMemo(() => {
    if (!multiple) return false
    return (
      values.length === 0 ||
      values.includes(allValue) ||
      values.length === options.filter((o) => o.value !== allValue).length
    )
  }, [multiple, values, allValue, options])

  const handleSingleSelect = (val: string) => {
    onValueChange?.(val)
    setOpen(false)
  }

  const handleMultiToggle = (val: string) => {
    if (!onValuesChange) return

    if (val === allValue) {
      onValuesChange([allValue])
      return
    }

    let newValues: string[]
    if (isAllSelected) {
      newValues = [val]
    } else if (values.includes(val)) {
      newValues = values.filter((v) => v !== val && v !== allValue)
      if (newValues.length === 0) {
        newValues = [allValue]
      }
    } else {
      newValues = [...values.filter((v) => v !== allValue), val]
      const nonAllOptions = options.filter((o) => o.value !== allValue)
      if (newValues.length === nonAllOptions.length) {
        newValues = [allValue]
      }
    }

    onValuesChange(newValues)
  }

  const handleSelectAll = () => {
    onValuesChange?.([allValue])
  }

  const handleClearAll = () => {
    onValuesChange?.([allValue])
  }

  // Label to display on the trigger button
  const triggerLabel = React.useMemo(() => {
    if (!multiple) {
      if (!value || value === allValue) {
        return allLabel || placeholder
      }
      const match = options.find((o) => o.value === value)
      return match ? match.label : placeholder
    }

    if (isAllSelected) {
      return allLabel || placeholder
    }

    if (values.length === 1) {
      const match = options.find((o) => o.value === values[0])
      return match ? match.label : (itemNounSingular ? `1 ${itemNounSingular}` : "1 seleccionado")
    }

    const count = values.length
    const noun = count === 1 ? (itemNounSingular || "opción") : (itemNounPlural || "opciones")
    return `${count} ${noun}`
  }, [multiple, value, values, isAllSelected, options, allValue, allLabel, placeholder, itemNounSingular, itemNounPlural])

  const selectedCountBadge = multiple && !isAllSelected && values.length > 1 ? values.length : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-8 px-2.5 text-xs font-normal justify-between bg-background gap-1.5 min-w-[130px] sm:min-w-[140px] hover:bg-accent/50",
            multiple && !isAllSelected && "border-primary/50 text-foreground font-medium",
            triggerClassName
          )}
        >
          <div className="flex items-center gap-1.5 truncate">
            {TriggerIcon && <TriggerIcon className="size-3 text-muted-foreground shrink-0" />}
            <span className="truncate">{triggerLabel}</span>
          </div>

          <div className="flex items-center gap-1 shrink-0 ml-1">
            {selectedCountBadge !== null && (
              <Badge
                variant="secondary"
                className="px-1.5 py-0 text-[10px] h-4 font-mono font-medium leading-none bg-primary/10 text-primary border-0"
              >
                {selectedCountBadge}
              </Badge>
            )}
            <ChevronsUpDown className="size-3 text-muted-foreground/70" />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align={align}
        className={cn("w-60 p-0 shadow-lg border-border/80", className)}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          
          {multiple && (
            <div className="flex items-center justify-between px-2.5 py-1.5 bg-muted/30 border-b border-border/40 text-[11px]">
              <span className="text-muted-foreground">
                {isAllSelected ? "Todos seleccionados" : `${values.length} seleccionados`}
              </span>
              {!isAllSelected ? (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-primary hover:underline font-medium cursor-pointer"
                >
                  Restablecer todos
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-muted-foreground hover:text-foreground cursor-pointer"
                >
                  Todos
                </button>
              )}
            </div>
          )}

          <CommandList className="max-h-56">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isAllOption = option.value === allValue
                let isSelected: boolean

                if (multiple) {
                  if (isAllOption) {
                    isSelected = isAllSelected
                  } else {
                    isSelected = !isAllSelected && values.includes(option.value)
                  }
                } else {
                  isSelected = (value || allValue) === option.value
                }

                const OptionIcon = option.icon

                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      if (multiple) {
                        handleMultiToggle(option.value)
                      } else {
                        handleSingleSelect(option.value)
                      }
                    }}
                    className={cn(
                      "flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs rounded-sm",
                      isSelected && "font-medium text-foreground"
                    )}
                  >
                    <div className="flex items-center gap-2 truncate">
                      {OptionIcon && <OptionIcon className="size-3 text-muted-foreground shrink-0" />}
                      <span className="truncate">{option.label}</span>
                    </div>

                    <Check
                      className={cn(
                        "size-3.5 text-primary stroke-[2.5] shrink-0",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
})

export default Combobox
